import { DEFAULT_HAND_RIGGING } from "../definitions/items/melees";
import { type WeaponDefinition } from "../definitions/loots";
import { RIG_GESTURES, type OperatorCarry, type OperatorRigState } from "./operatorRig";
import { Vec } from "./vector";
import { DefinitionType } from "./objectDefinitions";
import { operatorBodyPose, transformRigPoint } from "./operatorKinematics";

export const OPERATOR_WEAPON_SCALE = 0.62;

export interface OperatorMotion { moveAngle: number; weight: number; gesture: number; progress: number; }
export const OPERATOR_STILL: OperatorMotion = { moveAngle: 0, weight: 0, gesture: 0, progress: 0 };
/** Small shared grip/weapon translation; actions retain their original aim and recoil. */
export function operatorWeaponBob(pose: number, motion: OperatorMotion, weapon: WeaponDefinition): number {
    if (weapon.defType !== DefinitionType.Gun || motion.gesture !== 0 || motion.weight === 0) return 0;
    return Math.sin(pose / 254 * Math.PI * 2) * (motion.weight / 255) * 0.075;
}
/** Apply once to a fresh pose on client and server. Both grips follow the exact
 * rigid carry transform applied to the weapon container; the muzzle settles to aim on firing. */
export function applyOperatorRunStride(state: OperatorRigState, weapon: WeaponDefinition): void {
    if (state.gesture !== "ready" || state.moving === 0) return;
    state.runStride = state.moving;
    if (weapon.defType !== DefinitionType.Gun) return;
    const cycle = state.phase * Math.PI * 2;
    const beat = Math.sin(cycle) * state.moving;
    // Carry the weapon from the animated chest. Both hand targets receive this
    // same rigid transform, so the rear hand never stays behind a moving rifle.
    const body = operatorBodyPose(state);
    const anchor = transformRigPoint(body.chest, { x: 0.5, y: 0, z: 3.30 + body.bounce });
    const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
    const dx = ((anchor.x - 0.5) * Math.cos(yaw) - anchor.y * Math.sin(yaw)) * 1.15;
    const dy = ((anchor.x - 0.5) * Math.sin(yaw) + anchor.y * Math.cos(yaw)) * 0.54 - (anchor.z - 3.30) * 0.84;
    const rifle = state.carry === "twoHanded" && !weapon.isDual;
    const weight = state.moving;
    // The stock is carried near the shoulder, rocking about the rear grip.
    // A small pulse toward the chest lets both elbows compress on each landing.
    const sway = beat * (rifle ? 0.18 : 0.11);
    const pivot = (state.rightHand.x - (state.weaponBob ?? 0)) * (rifle ? 0.85 : 0.60);
    const tuck = rifle ? weight * (0.48 + (1 - Math.cos(cycle * 2)) * 0.10) : weight * 0.10;
    const bob = dx * Math.cos(state.rotation) + dy * Math.sin(state.rotation) - tuck + pivot * (1 - Math.cos(sway));
    const lift = -dx * Math.sin(state.rotation) + dy * Math.cos(state.rotation)
        + beat * (rifle ? 0.24 : 0.16) - pivot * Math.sin(sway);
    const c = Math.cos(sway), s = Math.sin(sway), previousBob = state.weaponBob ?? 0;
    for (let side = 0; side < 2; side++) {
        const hand = side === 0 ? state.leftHand : state.rightHand;
        const x = hand.x - previousBob, y = hand.y - previousBob * 0.65;
        hand.x = x * c - y * s + bob;
        hand.y = x * s + y * c + lift;
    }
    state.weaponBob = bob;
    state.weaponLift = lift;
    state.weaponSway = sway;
}
/** Derive carrying pose from the same equipped definition on client and server. */
export function operatorCarry(weapon: WeaponDefinition): OperatorCarry {
    if (weapon.idString === "fists") return "unarmed";
    if (weapon.defType === DefinitionType.Throwable) return "oneHanded";
    const fists = weapon.fists ?? DEFAULT_HAND_RIGGING;
    if (weapon.defType === DefinitionType.Gun) {
        // A coincident pistol grip can be carried in the firing hand. Dual guns
        // and separated rifle/support grips always keep both hands attached.
        return !weapon.isDual && fists.left.x === fists.right.x && fists.left.y === fists.right.y
            ? "oneHanded" : "twoHanded";
    }
    return Math.abs(fists.left.x - fists.right.x) > 12 ? "twoHanded" : "oneHanded";
}
export function operatorRigState(rotation: number, pose: number, motion: OperatorMotion, weapon: WeaponDefinition,
    helmet: number, vest: number, backpack: number, skinId = "raider_cobalt", animateGun = true): OperatorRigState {
    const fists = weapon.fists ?? DEFAULT_HAND_RIGGING;
    const offset = weapon.defType === DefinitionType.Gun && weapon.isDual ? weapon.leftRightOffset * 20 : 0;
    const leftHand = Vec.scale(Vec(fists.left.x, fists.left.y - offset), OPERATOR_WEAPON_SCALE / 20);
    // Slide into a compact support grip when moving, then hold that point on
    // the handguard throughout the carry cycle. The firing grip stays fixed.
    if (weapon.defType === DefinitionType.Gun && !weapon.isDual && leftHand.x > 2.1) {
        leftHand.x *= 0.88;
        leftHand.x += (Math.min(leftHand.x, 2.15) - leftHand.x) * (motion.weight / 255);
    }
    const weaponBob = animateGun ? operatorWeaponBob(pose, motion, weapon) : 0;
    leftHand.x += weaponBob;
    leftHand.y += weaponBob * 0.65;
    const rightHand = Vec.scale(Vec(fists.right.x, fists.right.y + offset), OPERATOR_WEAPON_SCALE / 20);
    rightHand.x += weaponBob;
    rightHand.y += weaponBob * 0.65;
    return {
        rotation, phase: pose / 254, moving: motion.weight / 255, moveAngle: motion.moveAngle,
        gesture: RIG_GESTURES[motion.gesture] ?? "ready", gesturePhase: motion.progress / 255,
        helmet, vest, backpack, skinId, leftHand, carry: operatorCarry(weapon),
        rightHand, weaponBob
    };
}
